import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaDownload, FaCheck } from 'react-icons/fa';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const { t } = useTranslation();
  const promptTimeoutRef = useRef(null);

  useEffect(() => {
    // Check if user has already dismissed or installed
    const hasInteracted = localStorage.getItem('pwaPromptInteracted');
    const lastDismissed = localStorage.getItem('pwaLastDismissed');
    const now = Date.now();
    const showAgainAfter = 24 * 60 * 60 * 1000; // 24 hours

    // Don't show if user installed or last dismissed was less than 24 hours ago
    if (hasInteracted === 'installed') return;
    if (lastDismissed && now - parseInt(lastDismissed) < showAgainAfter) return;

    const handler = (e) => {
      // Prevent the default prompt
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      // store on window so other components (footer button) can access it
      try {
        window.deferredPwaPrompt = e;
      } catch (err) {
        // ignore (non-browser environments)
      }
      
      // Show prompt after a 3 second delay
      promptTimeoutRef.current = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      localStorage.setItem('pwaPromptInteracted', 'installed');
      setShowPrompt(false);
      setDeferredPrompt(null);
      window.deferredPwaPrompt = null;
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (promptTimeoutRef.current) {
        clearTimeout(promptTimeoutRef.current);
      }
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      localStorage.setItem('pwaPromptInteracted', 'installed');
      setShowPrompt(false);
    } else {
      console.log('User dismissed the install prompt');
      localStorage.setItem('pwaLastDismissed', Date.now().toString());
      setShowPrompt(false);
    }

    // Clear the prompt
    setDeferredPrompt(null);
    try { window.deferredPwaPrompt = null; } catch (err) {}
    setIsInstalling(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwaLastDismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 bg-gradient-to-br from-cigar-dark/98 to-black/95 backdrop-blur-lg text-cigar-cream p-4 rounded-2xl shadow-2xl border border-cigar-gold/40 z-50 animate-[slideUp_0.4s_ease-out]">
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-cigar-cream/60 hover:text-cigar-cream transition-colors"
      >
        <FaTimes size={16} />
      </button>
      
      <div className="mb-4 pr-6">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cigar-gold/40 to-amber-500/30 flex items-center justify-center text-base flex-shrink-0">
            <FaDownload className="text-cigar-gold" />
          </div>
          <div>
            <h3 className="text-base font-bold text-cigar-cream">{t('pwa.installTitle')}</h3>
            <p className="text-sm text-cigar-cream/75 leading-relaxed mt-1">{t('pwa.installMessage')}</p>
          </div>
        </div>
      </div>

      {/* Feature Pills */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="text-[11px] bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
          <FaCheck className="text-cigar-gold text-xs" />
          <span className="text-cigar-cream/80">Offline Access</span>
        </div>
        <div className="text-[11px] bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
          <FaCheck className="text-cigar-gold text-xs" />
          <span className="text-cigar-cream/80">Fast Loading</span>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleDismiss}
          className="flex-1 px-4 py-2.5 text-sm text-cigar-cream/70 hover:text-cigar-cream border border-white/20 rounded-lg transition-colors font-medium"
        >
          {t('pwa.later')}
        </button>
        <button
          onClick={handleInstall}
          disabled={isInstalling}
          className="flex-1 px-4 py-2.5 text-sm bg-gradient-to-r from-cigar-gold to-amber-500 text-cigar-dark font-bold rounded-lg hover:shadow-[0_8px_20px_rgba(212,175,55,0.4)] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isInstalling ? (
            <>
              <span className="animate-spin inline-block">⟳</span>
              Installing...
            </>
          ) : (
            <>
              <FaDownload size={14} />
              {t('pwa.install')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default InstallPWA;